import { useMemo } from "react";
import { useTran } from "@/hooks";
import { InputField } from "@/components/common";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";

const resolveOptions = (question) => {
  if (Array.isArray(question?.options)) {
    return question.options.map((opt) => ({
      value: opt.value ?? opt.label ?? "",
      label: opt.label ?? opt.value ?? "",
    }));
  }
  return [];
};

const SurveyQuestionField = ({ question, value, onChange }) => {
  const { t } = useTran();
  const questionType = question?.questionType || "text";
  const options = useMemo(() => resolveOptions(question), [question]);

  if (questionType === "long_text") {
    return (
      <Textarea
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t("Enter a detailed answer...")}
      />
    );
  }

  if (["text", "email"].includes(questionType)) {
    return (
      <InputField
        type={questionType === "email" ? "email" : "text"}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t("Enter your answer...")}
      />
    );
  }

  if (
    ["number", "currency", "percent", "rating", "scale"].includes(questionType)
  ) {
    return (
      <InputField
        type="number"
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t("Enter a number")}
      />
    );
  }

  if (questionType === "date") {
    return (
      <InputField
        type="date"
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  if (["yes_no", "true_false"].includes(questionType)) {
    const yesLabel = questionType === "true_false" ? t("True") : t("Yes");
    const noLabel = questionType === "true_false" ? t("False") : t("No");
    return (
      <RadioGroup
        className="grid gap-3"
        value={value || ""}
        onValueChange={onChange}
      >
        {[
          { label: yesLabel, value: "yes" },
          { label: noLabel, value: "no" },
        ].map((opt) => (
          <label
            key={opt.value}
            className={cn(
              "flex items-center gap-2 rounded-radius border border-border bg-card px-3 py-2 text-sm",
              value === opt.value && "border-primary/50",
            )}
          >
            <RadioGroupItem value={opt.value} />
            <span className="text-foreground">{opt.label}</span>
          </label>
        ))}
      </RadioGroup>
    );
  }

  if (questionType === "multiple_choice") {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="grid gap-2">
        {options.map((opt) => {
          const checked = selected.includes(opt.value);
          return (
            <label
              key={opt.value}
              className="flex items-center gap-2 rounded-radius border border-border bg-card px-3 py-2 text-sm"
            >
              <Checkbox
                checked={checked}
                onCheckedChange={(nextChecked) => {
                  if (nextChecked) {
                    onChange([...selected, opt.value]);
                  } else {
                    onChange(selected.filter((item) => item !== opt.value));
                  }
                }}
              />
              <span className="text-foreground">{opt.label}</span>
            </label>
          );
        })}
      </div>
    );
  }

  if (questionType === "single_choice") {
    return (
      <RadioGroup
        className="grid gap-3"
        value={value || ""}
        onValueChange={onChange}
      >
        {options.map((opt) => (
          <label
            key={opt.value}
            className={cn(
              "flex items-center gap-2 rounded-radius border border-border bg-card px-3 py-2 text-sm",
              value === opt.value && "border-primary/50",
            )}
          >
            <RadioGroupItem value={opt.value} />
            <span className="text-foreground">{opt.label}</span>
          </label>
        ))}
      </RadioGroup>
    );
  }

  return (
    <InputField
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      placeholder={t("Enter your answer...")}
    />
  );
};

export default SurveyQuestionField;
